/**
 * Pure economics helpers used by the Projects ↔ Accounting bridge.
 *
 * Extracted from the legacy `bridgeStore` so they can be reused by derived
 * hooks (`useBridge`) and by the EconomiaSection of ProjectsView without
 * pulling in the store. All amounts are in cents.
 */

import type { TimeEntry, CentsAmount } from '../domain/models';

const VAT_RATE_DEFAULT = 0.22; // Italian VAT default

/**
 * Sum the cost (in cents) of all time entries.
 * `costRate` is already in cents/hour.
 */
export function calculateTimeEntriesCost(entries: TimeEntry[]): CentsAmount {
  return entries.reduce((sum, te) => {
    const hours = te.minutes / 60;
    const cost = hours * te.costRate;
    return sum + Math.round(cost);
  }, 0);
}

/**
 * Sum the potential revenue (in cents) of billable time entries.
 * `billableRate` is already in cents/hour.
 */
export function calculateBillableRevenue(entries: TimeEntry[]): CentsAmount {
  return entries
    .filter((te) => te.billable)
    .reduce((sum, te) => {
      const hours = te.minutes / 60;
      const revenue = hours * te.billableRate;
      return sum + Math.round(revenue);
    }, 0);
}

/**
 * Average burn rate (cents per hour) across the given time entries.
 * Returns 0 if no entries or zero minutes.
 */
export function calculateBurnRate(entries: TimeEntry[]): CentsAmount {
  if (entries.length === 0) return 0;

  const totalCost = calculateTimeEntriesCost(entries);
  const totalMinutes = entries.reduce((sum, te) => sum + te.minutes, 0);

  if (totalMinutes === 0) return 0;

  return Math.round((totalCost / totalMinutes) * 60);
}

/**
 * Apply Italian VAT (default 22%) to a cents subtotal.
 */
export function applyVat(subtotal: CentsAmount, vatRate: number = VAT_RATE_DEFAULT): {
  vatTotal: CentsAmount;
  total: CentsAmount;
} {
  const vatTotal = Math.round(subtotal * vatRate);
  return { vatTotal, total: subtotal + vatTotal };
}

/**
 * Suggest an invoice number for the current year (e.g. "2026/042").
 * Used as a hint for the user; the actual number is finalized on save.
 */
export function generateInvoiceNumber(): string {
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${year}/${random}`;
}
